import React, { useEffect, useRef, useState } from "react";
import { useField } from "formik";
import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import ReactCrop, {
    centerCrop,
    makeAspectCrop,
    PercentCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { FileHelper } from "../../../../utils/helpers";
import { ImageFileDto } from "../../../../utils/lib/imageFileDto";

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.05;

interface FormImageCropProps {
    name: string;
    loading?: boolean;
    aspect?: number;
    cropWidth?: number;
    maxOutputWidth?: number;
    maxOutputSize?: number;
    modalTitle?: string;
    chooseText?: string;
    changeText?: string;
    outputMimeType?: string;
    outputQuality?: number;
    previewMaxHeight?: number;
    onChange?: (image?: ImageFileDto | null) => void;
}

const getImageSource = (image?: ImageFileDto | null): string | undefined =>
    image ? FileHelper.FileToBase64String({ image }) : undefined;

const getCenteredCrop = (
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
    cropWidth: number,
    scale: number
): PercentCrop => {
    if (scale < 1) {
        const width = cropWidth / scale;
        const height = ((width / 100) * mediaWidth / aspect / mediaHeight) * 100;

        return {
            unit: "%",
            x: (100 - width) / 2,
            y: (100 - height) / 2,
            width,
            height,
        };
    }

    return centerCrop(
        makeAspectCrop(
            {
                unit: "%",
                width: cropWidth / scale,
            },
            aspect,
            mediaWidth,
            mediaHeight
        ),
        mediaWidth,
        mediaHeight
    );
};

const getCanvasMimeType = (mimeType?: string): string => {
    const supportedMimeTypes = ["image/jpeg", "image/png", "image/webp"];
    return mimeType && supportedMimeTypes.includes(mimeType)
        ? mimeType
        : "image/png";
};

const getOutputMimeType = (
    image: ImageFileDto,
    outputMimeType?: string
): string => {
    const extension = image.extension?.toLowerCase();
    return getCanvasMimeType(
        outputMimeType ||
        image.mimeType ||
        (extension ? FileHelper.extensionToMime[extension] : undefined)
    );
};

const getExtensionFromMimeType = (mimeType: string): string =>
    mimeType === "image/jpeg" ? ".jpg" : `.${mimeType.split("/")[1] ?? "png"}`;

const getContentSize = (content: string): number =>
    Math.ceil((content.length * 3) / 4);

const getCanvasDataUrl = (
    canvas: HTMLCanvasElement,
    mimeType: string,
    initialQuality: number,
    maxOutputSize: number
): string => {
    const qualitySupported =
        mimeType === "image/jpeg" || mimeType === "image/webp";
    let quality = initialQuality;
    let dataUrl = canvas.toDataURL(mimeType, quality);

    while (
        qualitySupported &&
        getContentSize(dataUrl.split(",")[1] ?? "") > maxOutputSize &&
        quality > 0.6
    ) {
        quality = Math.max(0.6, quality - 0.08);
        dataUrl = canvas.toDataURL(mimeType, quality);
    }

    return dataUrl;
};

const loadImage = (source: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = source;
    });

const getPaddedImageSource = async (
    source: string,
    scale: number
): Promise<string> => {
    if (scale >= 1) {
        return source;
    }

    try {
        const sourceImage = await loadImage(source);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(sourceImage.naturalWidth / scale));
        canvas.height = Math.max(1, Math.round(sourceImage.naturalHeight));

        const context = canvas.getContext("2d");
        if (!context) {
            return source;
        }

        context.drawImage(
            sourceImage,
            (canvas.width - sourceImage.naturalWidth) / 2,
            0
        );

        return canvas.toDataURL("image/png");
    } catch (error) {
        // Two known failure modes here: the image fails to load at all, or it's
        // cross-origin without CORS headers and taints the canvas (SecurityError
        // on toDataURL). Either way, padding is a nice-to-have for the zoomed-out
        // crop preview -- falling back to the original, unpadded source is far
        // better than crashing the whole edit modal.
        console.warn("Unable to pad image for cropping; using original source.", error);
        return source;
    }
};

export const FormImageCrop = (props: FormImageCropProps) => {
    const aspect = props.aspect ?? 16 / 9;
    const cropWidth = props.cropWidth ?? 92;
    const maxOutputWidth = props.maxOutputWidth ?? 1400;
    const maxOutputSize = props.maxOutputSize ?? 2 * 1024 * 1024;
    const outputQuality = props.outputQuality ?? 0.84;
    const modalTitle = props.modalTitle ?? "Image Crop";
    const chooseText = props.chooseText ?? "Choose Image";
    const previewMaxHeight = props.previewMaxHeight ?? 220;
    const previewWidth = Math.round(previewMaxHeight * aspect);

    const [, meta, helpers] = useField<ImageFileDto | null | undefined>(
        props.name
    );
    const image = meta.value;
    const imageSource = getImageSource(image);
    const imageRef = useRef<HTMLImageElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pendingImage, setPendingImage] = useState<ImageFileDto | null>();
    const [crop, setCrop] = useState<PercentCrop | undefined>();
    const [scale, setScale] = useState<number>(MIN_SCALE);
    const imageToEdit = pendingImage ?? image;
    const imageToEditSource = getImageSource(imageToEdit);
    const [cropSource, setCropSource] = useState<string | undefined>(imageToEditSource);
    const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);

    const setCropForImage = (imageElement: HTMLImageElement, nextScale: number) => {
        setCrop(
            getCenteredCrop(
                imageElement.naturalWidth,
                imageElement.naturalHeight,
                aspect,
                cropWidth,
                nextScale
            )
        );
    };

    useEffect(() => {
        const imageElement = imageRef.current;
        if (!imageElement || !cropSource) return;

        setCropForImage(imageElement, scale < 1 ? 1 : scale);
    }, [scale, cropSource]);

    useEffect(() => {
        let isCancelled = false;

        async function setNextCropSource() {
            if (!imageToEditSource) {
                setCropSource(undefined);
                return;
            }

            const nextCropSource = await getPaddedImageSource(imageToEditSource, scale);
            if (!isCancelled) {
                setCropSource(nextCropSource);
            }
        }

        setNextCropSource();

        return () => {
            isCancelled = true;
        };
    }, [imageToEditSource, scale]);

    const cropImage = async (
        imageToCrop?: ImageFileDto | null
    ): Promise<ImageFileDto | null | undefined> => {
        if (!imageToCrop || !crop || !imageRef.current) {
            return imageToCrop;
        }

        const sourceImage = imageRef.current;
        const canvas = document.createElement("canvas");
        const cropX = (crop.x / 100) * sourceImage.naturalWidth;
        const cropY = (crop.y / 100) * sourceImage.naturalHeight;
        const cropWidthPx = (crop.width / 100) * sourceImage.naturalWidth;
        const cropHeightPx = (crop.height / 100) * sourceImage.naturalHeight;

        const outputScale = Math.min(1, maxOutputWidth / cropWidthPx);
        canvas.width = Math.max(1, Math.round(cropWidthPx * outputScale));
        canvas.height = Math.max(1, Math.round(cropHeightPx * outputScale));

        const context = canvas.getContext("2d");
        if (!context) {
            return imageToCrop;
        }

        const mimeType = getOutputMimeType(imageToCrop, props.outputMimeType);

        if (mimeType === "image/jpeg") {
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, canvas.width, canvas.height);
        }

        const sourceX = Math.max(0, cropX);
        const sourceY = Math.max(0, cropY);
        const sourceRight = Math.min(
            sourceImage.naturalWidth,
            cropX + cropWidthPx
        );
        const sourceBottom = Math.min(
            sourceImage.naturalHeight,
            cropY + cropHeightPx
        );
        const sourceWidth = sourceRight - sourceX;
        const sourceHeight = sourceBottom - sourceY;
        if (sourceWidth > 0 && sourceHeight > 0) {
            context.drawImage(
                sourceImage,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                (sourceX - cropX) * outputScale,
                (sourceY - cropY) * outputScale,
                sourceWidth * outputScale,
                sourceHeight * outputScale
            );
        }

        const dataUrl = getCanvasDataUrl(
            canvas,
            mimeType,
            outputQuality,
            maxOutputSize
        );
        const content = dataUrl.split(",")[1] ?? imageToCrop.content;

        return {
            ...imageToCrop,
            mimeType,
            extension: getExtensionFromMimeType(mimeType),
            content,
            imageSize: getContentSize(content),
        };
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const nextImage = await FileHelper.FileToImageFileDto(file);
        setPendingImage(nextImage);
        setCrop(undefined);
        setScale(MIN_SCALE);
        setIsCropModalOpen(true);
        event.target.value = "";
    };

    const openFilePicker = () => {
        if (props.loading) return;
        fileInputRef.current?.click();
    };

    const handleImageAreaKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
        }
    };

    const clearImage = async () => {
        await helpers.setValue(null, true);
        props.onChange?.(null);
        setPendingImage(undefined);
        setCrop(undefined);
        setScale(MIN_SCALE);
        setIsCropModalOpen(false);
    };

    const cancelCrop = () => {
        setPendingImage(undefined);
        setCrop(undefined);
        setScale(MIN_SCALE);
        setIsCropModalOpen(false);
    };

    const applyCrop = async () => {
        const croppedImage = await cropImage(imageToEdit);
        await helpers.setValue(croppedImage, true);
        props.onChange?.(croppedImage);
        setPendingImage(undefined);
        setCrop(undefined);
        setScale(MIN_SCALE);
        setIsCropModalOpen(false);
    };

    return (
        <>
            <Input
                innerRef={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={props.loading}
                style={{ display: "none" }}
            />

            {!imageSource && (
                <div
                    role="button"
                    tabIndex={props.loading ? -1 : 0}
                    onClick={openFilePicker}
                    onKeyDown={handleImageAreaKeyDown}
                    style={{
                        border: "1px dashed #adb5bd",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 12,
                        color: "#6c757d",
                        cursor: props.loading ? "default" : "pointer",
                        height: previewMaxHeight,
                        width: previewWidth,
                        maxWidth: "100%",
                    }}
                >
                    <i className="bi bi-image" style={{ fontSize: 28 }} />
                    <span className="btn btn-outline-primary">
                        {chooseText}
                    </span>
                </div>
            )}

            {imageSource && (
                <>
                    <div
                        role="button"
                        tabIndex={props.loading ? -1 : 0}
                        onClick={openFilePicker}
                        onKeyDown={handleImageAreaKeyDown}
                        style={{
                            borderRadius: 6,
                            overflow: "hidden",
                            border: "1px solid #dee2e6",
                            background: "#f8f9fa",
                            cursor: props.loading ? "default" : "pointer",
                            position: "relative",
                            height: previewMaxHeight,
                            width: previewWidth,
                            maxWidth: "100%",
                        }}
                    >
                        <Button
                            type="button"
                            color="secondary"
                            disabled={props.loading}
                            onClick={(event) => {
                                event.stopPropagation();
                                clearImage();
                            }}
                            aria-label="Remove image"
                            title="Remove image"
                            style={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                width: 32,
                                height: 32,
                                padding: 0,
                                border: "none",
                                borderRadius: 4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(0, 0, 0, 0.55)",
                                color: "#f1f1f1",
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.25)",
                            }}
                        >
                            <i className="bi bi-x-lg" />
                        </Button>
                        <img
                            alt=""
                            src={imageSource}
                            style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                        />
                    </div>
                </>
            )}

            <Modal
                isOpen={isCropModalOpen && !!imageToEditSource}
                toggle={cancelCrop}
                size="lg"
                centered
            >
                <ModalHeader toggle={cancelCrop}>
                    {modalTitle}
                </ModalHeader>
                <ModalBody>
                    {cropSource && (
                        <>
                            <div
                                style={{
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    border: "1px solid #dee2e6",
                                    background: "#f8f9fa",
                                }}
                            >
                                <ReactCrop
                                    crop={crop}
                                    aspect={aspect}
                                    locked
                                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                                >
                                    <img
                                        ref={imageRef}
                                        alt=""
                                        src={cropSource}
                                        onLoad={(event) =>
                                            setCropForImage(
                                                event.currentTarget,
                                                scale < 1 ? 1 : scale
                                            )
                                        }
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            maxHeight: 520,
                                            objectFit: "contain",
                                        }}
                                    />
                                </ReactCrop>
                            </div>
                            <div className="mt-3">
                                <Label for={`${props.name}Scale`} className="mb-1">
                                    Scale
                                </Label>
                                <Input
                                    id={`${props.name}Scale`}
                                    type="range"
                                    min={MIN_SCALE}
                                    max={MAX_SCALE}
                                    step={SCALE_STEP}
                                    value={scale}
                                    onChange={(event) => setScale(Number(event.target.value))}
                                />
                            </div>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        type="button"
                        color="secondary"
                        outline
                        onClick={cancelCrop}
                    >
                        Cancel
                    </Button>
                    <Button type="button" color="primary" onClick={applyCrop}>
                        Done
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};